/**
 * Duplicate Report Dialog Component (Reports v2)
 *
 * Single name input defaulting to "<Original Name> (Copy)". Confirms a
 * `POST /reporting/dashboards/{id}/duplicate/` call. The parent should
 * handle navigation to the new report once the API resolves.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Loader2 } from 'lucide-react';
import { type AnalyseDashboard } from '../../../lib/api/reporting';

interface DuplicateReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: AnalyseDashboard | null;
  saving: boolean;
  onSubmit: (newName: string) => void;
}

export function DuplicateReportDialog({
  open,
  onOpenChange,
  report,
  saving,
  onSubmit,
}: DuplicateReportDialogProps) {
  const [name, setName] = useState('');

  useEffect(() => {
    if (report && open) {
      setName(`${report.name} (Copy)`);
    }
  }, [report, open]);

  const handleConfirm = () => {
    if (!report || !name.trim()) return;
    onSubmit(name.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Duplicate Report</DialogTitle>
          <DialogDescription>
            Create a copy of "{report?.name}" including all of its charts. The duplicate
            starts as private.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>New Report Name *</Label>
            <Input
              placeholder="Enter name for the duplicate"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!name.trim() || saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Duplicate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
